import {isNil} from 'ramda';
import {isNilOrEmpty, isNonEmptyString} from 'ramda-adjunct';

import type {AppSyncHostResolution} from '../types';

// `buildWebSocketUrl` derives the realtime endpoint by rewriting `https://` to `wss://` and
// `http://` to `ws://`, so only these schemes yield a URL the WebSocket constructor accepts.
const SECURE_PROTOCOLS = new Set(['https:', 'wss:']);
// Unencrypted schemes reach only the backend `appsync-mock` on the developer's machine: a local
// build and an exact loopback host. `*.localhost` is excluded because only some browsers pin it
// to loopback. GitLab CI makes every deployed build, so each one keeps requiring https:/wss:.
// `NODE_ENV` cannot tell them apart: the deployed dev and greek builds use development mode.
const LOOPBACK_ONLY_PROTOCOLS = new Set(['http:', 'ws:']);
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

const isLocalBuild = () => isNilOrEmpty(process.env.GITLAB_CI);

const isSupportedProtocol = (url: URL) =>
  SECURE_PROTOCOLS.has(url.protocol) ||
  (isLocalBuild() && LOOPBACK_ONLY_PROTOCOLS.has(url.protocol) && LOOPBACK_HOSTS.has(url.hostname));

const parseUrl = (appSyncUrl: string) => {
  try {
    return new URL(appSyncUrl);
  } catch {
    return null;
  }
};

/**
 * Single source of truth for `APPSYNC_URL` validation. Pure — it only
 * classifies, so both the auth header and the WebSocket URL builders agree on
 * whether the configuration is usable, absent, or broken.
 *
 * A URL that parses but has an unsupported scheme or no host (for example
 * `file:///tmp/appsync`) is malformed too: the WebSocket constructor would throw
 * on it, and the reconnect loop must treat that as a deterministic fault. The
 * same holds for `http:`/`ws:` outside a local build or to a non-loopback host.
 */
export const resolveAppSyncHost = (appSyncUrl: string | undefined): AppSyncHostResolution => {
  if (isNil(appSyncUrl) || appSyncUrl === '') {
    return {status: 'notConfigured'};
  }

  const url = parseUrl(appSyncUrl);

  if (isNil(url) || !isSupportedProtocol(url) || !isNonEmptyString(url.host)) {
    return {status: 'malformed'};
  }

  return {status: 'resolved', host: url.host, appSyncUrl};
};
